import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faWarehouse, faBoxOpen } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import CoilSplitPanel from '../coils/CoilSplitPanel';

const WarehouseInventoryDetails = ({ isOpen, onClose, inventory }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  if (!inventory) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
        <div className="bg-white p-8 rounded-lg shadow-2xl max-w-3xl w-full">
          <h2 className="text-xl font-bold mb-4">{t('warehouseInventory.view.loading')}</h2>
        </div>
      </div>
    );
  }

  const {
    product_name,
    alias_name,
    product_size,
    product_weight,
    total_stock_all_warehouses,
    total_pending_all_warehouses,
    total_pending_purchase_all_warehouses,
    total_available_all_warehouses,
    warehouse_count,
    warehouse_breakdown = [],
    product_size_id,
    coil_tracked,
    track_by_pieces,
  } = inventory;

  // When stock is a piece count, label the balances "pcs" so the numbers aren't
  // read as kilograms. kg-tracked sizes keep their unlabelled display.
  const stockUnit = track_by_pieces ? t('pieces.inventory.unitPieces') : '';

  const renderDetailItem = (label, value, unit = '') => (
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-lg font-semibold text-gray-800">
        {value ?? t('common.symbols.emDash')} {unit}
      </p>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        <header className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50 rounded-t-2xl">
          <div className="flex items-center gap-4">
            <FontAwesomeIcon icon={faBoxOpen} className="text-2xl text-blue-600" />
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold text-gray-800">{product_name}</h2>
                {track_by_pieces && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-sky-50 text-sky-700 border border-sky-200">
                    {t('pieces.productSize.trackByPieces.badge')}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">{alias_name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors rounded-full p-2 -mr-2"
            aria-label={t('common.buttons.close')}
          >
            <FontAwesomeIcon icon={faTimes} size="lg" />
          </button>
        </header>

        <main className="p-6 overflow-y-auto flex-grow">
          {/* Product Summary */}
          <section className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-4 border-b pb-2">
              {t('productSizes.view.header.sectionTitle')}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {renderDetailItem(t('productSizes.view.fields.productSize'), product_size)}
              {renderDetailItem(t('productSizes.view.fields.productWeight'), product_weight, t('common.units.kg'))}
              {renderDetailItem(t('warehouseInventory.table.columns.total_stock_all_warehouses'), total_stock_all_warehouses, stockUnit)}
              {renderDetailItem(t('warehouseInventory.table.columns.total_pending_all_warehouses'), total_pending_all_warehouses, stockUnit)}
              {renderDetailItem((t('warehouseInventory.table.columns.total_pending_purchase_all_warehouses')), total_pending_purchase_all_warehouses, stockUnit)}
              {renderDetailItem(t('warehouseInventory.table.columns.total_available_all_warehouses'), total_available_all_warehouses, stockUnit)}
              {renderDetailItem(t('warehouseInventory.table.columns.warehouse_count'), warehouse_count)}
            </div>
          </section>

          {/* Warehouse Breakdown */}
          <section>
            <h3 className="text-lg font-semibold text-gray-700 mb-4 border-b pb-2 flex items-center gap-2">
              <FontAwesomeIcon icon={faWarehouse} className="text-gray-500" />
              {t('productSizes.distribution.header.sectionTitle')}
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('warehouseInventory.view.productTable.columns.warehouse')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('warehouseInventory.view.productTable.columns.stockQuantity')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('orders.view.fields.pendingOrder')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('warehouseInventory.view.productTable.columns.pendingPurchaseOrder')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('warehouseInventory.view.productTable.columns.availableStock')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('warehouseInventory.view.productTable.columns.minimumStock')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('warehouseInventory.view.productTable.columns.lowStock')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {warehouse_breakdown.length > 0 ? (
                    warehouse_breakdown.flatMap((wh) => [
                      <tr key={wh.warehouse_id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{wh.warehouse_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{wh.stock_quantity}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{wh.pending_order}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{wh.pending_purchase_order ?? t('common.symbols.emDash')}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{wh.available_inventory}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{wh.minimum_stock}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            wh.is_low_stock
                              ? 'bg-red-100 text-red-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {wh.is_low_stock ? t('productSizes.table.copy.statuses.low_stock') : t('productSizes.table.copy.statuses.in_stock')}
                          </span>
                        </td>
                      </tr>,
                      // For a coil-tracked product the quantity above is a sum; this is
                      // what it is a sum OF. Rendered as its own full-width row so the
                      // ordinary column layout is untouched for every other product.
                      coil_tracked ? (
                        <tr key={`${wh.warehouse_id}-coils`}>
                          <td colSpan="7" className="px-6 pb-4 pt-0">
                            <CoilSplitPanel
                              productSizeId={product_size_id}
                              warehouseId={wh.warehouse_id}
                              expectedKg={wh.stock_quantity}
                            />
                          </td>
                        </tr>
                      ) : null,
                    ])
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-10 text-gray-500">
                        {t('productSizes.distribution.empty.message')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </main>

        <footer className="p-4 bg-gray-50 border-t border-gray-200 rounded-b-2xl flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            {t('common.buttons.close')}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default WarehouseInventoryDetails;
